import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const field =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-4 text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-white";

export default function ContactForm({ api, card }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${api}/enquiries`, form);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-6xl px-6 py-28 md:px-10 md:py-40" data-testid="contact-section">
      <motion.div
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-80px" }}
        className="mb-10 flex items-center gap-4"
      >
        <span className="font-mono-accent text-xs uppercase tracking-[0.35em] text-white/50">
          02 // Say hello
        </span>
        <span className="h-px flex-1 bg-white/10" />
      </motion.div>

      <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="md:col-span-5"
        >
          <h2 className="font-display text-5xl font-medium leading-[0.95] tracking-tighter md:text-6xl">
            Let's build<br />something.
          </h2>
          <p className="mt-6 max-w-sm text-white/60">
            Have a project, a question, or a consultancy enquiry? Send a note and it
            reaches the {card.company} team at{" "}
            <span className="text-white/80">{card.enquiryEmail}</span>.
          </p>
        </motion.div>

        <motion.form
          onSubmit={submit}
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="crystal rounded-3xl p-8 md:col-span-7 md:p-10"
          data-testid="enquiry-form"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <input
              className={field}
              placeholder="Your name"
              value={form.name}
              onChange={change("name")}
              data-testid="enquiry-name-input"
            />
            <input
              className={field}
              type="email"
              placeholder="Your email"
              value={form.email}
              onChange={change("email")}
              data-testid="enquiry-email-input"
            />
          </div>
          <textarea
            className={`${field} mt-6 min-h-[120px] resize-none`}
            placeholder="Your message"
            value={form.message}
            onChange={change("message")}
            data-testid="enquiry-message-input"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="enquiry-submit-btn"
            className="mt-8 flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-medium text-[#050505] transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {loading ? "Sending..." : "Send message"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
